
import { useState } from 'react';

const BusinessPlan = () => {
  const [activeTab, setActiveTab] = useState(0);

  const marketData = [
    {
      title: 'Domestic Market',
      value: '$200M',
      description: 'Annual water meter replacement market size',
      icon: 'ri-map-pin-line',
    },
    {
      title: 'Global Market',
      value: '$12B',
      description: 'Global smart meter market size',
      icon: 'ri-global-line',
    },
    {
      title: 'Growth Rate',
      value: '18.5%',
      description: 'Compound Annual Growth Rate (CAGR)',
      icon: 'ri-line-chart-line',
    },
  ];

  const salesMethods = [
    {
      step: 'Phase 1',
      title: 'Direct Government Sales',
      description:
        'Direct sales and proposals to water utilities starting from Gwangju Metropolitan City, expanding to nationwide local governments',
      icon: 'ri-building-line',
      color: 'from-teal-500 to-teal-600',
    },
    {
      step: 'Phase 2',
      title: 'Construction Partnership',
      description:
        'Bulk supply contracts for smart meters in new apartments and large building construction',
      icon: 'ri-community-line',
      color: 'from-teal-600 to-cyan-600',
    },
    {
      step: 'Phase 3',
      title: 'Distribution Expansion',
      description:
        'Entry into nationwide facility material distributors and online B2B platforms',
      icon: 'ri-store-line',
      color: 'from-cyan-500 to-cyan-600',
    },
    {
      step: 'Phase 4',
      title: 'Export',
      description: 'Participation in smart city projects in Southeast Asia and Middle East regions',
      icon: 'ri-ship-line',
      color: 'from-cyan-600 to-teal-700',
    },
  ];

  const productionPlan = [
    {
      year: '2024',
      target: '10,000 units',
      market: 'Gwangju City Pilot Project',
      revenue: '$1.2M',
    },
    {
      year: '2025',
      target: '50,000 units',
      market: 'Jeonnam/Jeonbuk Region Expansion',
      revenue: '$6M',
    },
    {
      year: '2026',
      target: '200,000 units',
      market: 'Major Cities Nationwide',
      revenue: '$24M',
    },
    {
      year: '2027',
      target: '500,000 units',
      market: 'Nationwide + International',
      revenue: '$60M',
    },
  ];

  const tabs = ['Market Status', 'Sales Strategy', 'Production Plan'];

  return (
    <section
      id="business-plan"
      className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-block px-5 py-2 bg-teal-50 border border-teal-200 rounded-full mb-4">
            <span className="text-teal-600 text-xs md:text-sm font-bold uppercase tracking-wider">Business Plan</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-3 md:mb-4">
            Business Plan &amp; Sales Strategy
          </h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto font-medium">
            Leading the smart water meter market with systematic market entry strategy and phased growth plan
          </p>
        </div>

        {/* Mobile Tab Navigation */}
        <div className="flex md:hidden bg-gray-100 rounded-full p-1 mb-8">
          {tabs.map((tab, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={`flex-1 py-2 px-1 rounded-full text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === index
                  ? 'bg-teal-600 text-white shadow-md'
                  : 'text-gray-600'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Market Status */}
        <div
          className={`mb-12 md:mb-20 ${
            activeTab !== 0 ? 'hidden md:block' : ''
          }`}
        >
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center hidden md:block">
            Sales & Market Status
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-8">
            {marketData.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-5 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-12 md:w-16 h-12 md:h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 md:mb-6">
                  <i
                    className={`${item.icon} text-2xl md:text-3xl text-white`}
                  ></i>
                </div>
                <h4 className="text-sm md:text-lg font-semibold text-gray-700 mb-1 md:mb-2">
                  {item.title}
                </h4>
                <div className="text-2xl md:text-4xl font-bold text-teal-600 mb-2 md:mb-3">
                  {item.value}
                </div>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Sales Methods */}
        <div
          className={`mb-12 md:mb-20 ${
            activeTab !== 1 ? 'hidden md:block' : ''
          }`}
        >
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center hidden md:block">
            Key Sales Methods
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
            {salesMethods.map((method, index) => (
              <div
                key={index}
                className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group"
              >
                <div
                  className={`w-10 md:w-14 h-10 md:h-14 bg-gradient-to-br ${method.color} rounded-lg md:rounded-xl flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300`}
                >
                  <i
                    className={`${method.icon} text-lg md:text-2xl text-white`}
                  ></i>
                </div>
                <div className="text-xs md:text-sm font-bold text-teal-600 mb-1 md:mb-2">
                  {method.step}
                </div>
                <h4 className="text-sm md:text-lg font-bold text-gray-900 mb-2 md:mb-3">
                  {method.title}
                </h4>
                <p className="text-[10px] md:text-sm text-gray-600 leading-relaxed">
                  {method.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Production Plan */}
        <div
          className={`${
            activeTab !== 2 ? 'hidden md:block' : ''
          }`}
        >
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center hidden md:block">
            Additional Business Production Plan
          </h3>
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[400px]">
                <thead>
                  <tr className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
                    <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-bold">
                      Year
                    </th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-bold">
                      Production Target
                    </th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-bold">
                      Target Market
                    </th>
                    <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-bold">
                      Expected Revenue
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {productionPlan.map((plan, index) => (
                    <tr
                      key={index}
                      className={`${
                        index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                      } hover:bg-teal-50 transition-colors duration-200`}
                    >
                      <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-bold text-teal-600">
                        {plan.year}
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-semibold text-gray-900">
                        {plan.target}
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm text-gray-700">
                        {plan.market}
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-bold text-teal-600">
                        {plan.revenue}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Production Plan Highlights */}
          <div className="mt-6 md:mt-8 grid grid-cols-3 gap-3 md:gap-6">
            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 md:p-6 text-white">
              <div className="text-[10px] md:text-sm font-semibold mb-1 md:mb-2 opacity-90">
                4-Year Cumulative Production
              </div>
              <div className="text-lg md:text-3xl font-bold">760,000 units</div>
            </div>
            <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl p-4 md:p-6 text-white">
              <div className="text-[10px] md:text-sm font-semibold mb-1 md:mb-2 opacity-90">
                4-Year Cumulative Revenue
              </div>
              <div className="text-lg md:text-3xl font-bold">$91.2M</div>
            </div>
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 md:p-6 text-white">
              <div className="text-[10px] md:text-sm font-semibold mb-1 md:mb-2 opacity-90">
                Average Annual Growth
              </div>
              <div className="text-lg md:text-3xl font-bold">187%</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessPlan;
